import React, { useEffect, useState, Suspense, lazy, createElement } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout/Layout';
import { ErrorBoundary } from './components/UI/ErrorBoundary';
import { PageLoader } from './components/UI/PageLoader';
import { Dialog } from './components/UI/Dialog';
import { HostSalonDialog } from './components/UI/HostSalonDialog';
import { PaymentDialog } from './components/UI/PaymentDialog';
import { CorporateTrainingDialog } from './components/UI/CorporateTrainingDialog';
import './index.css';
// Route prefetch helpers — call these on hover/intent to preload chunks
export const prefetchRoutes = {
  class: () => import('./pages/AIClass'),
  lounge: () => import('./pages/LoungeOnboard'),
  training: () => import('./pages/Training'),
  events: () => import('./pages/Events')
};
// Lazy-loaded pages — only loaded when the route is visited
const Home = lazy(() => import('./pages/Home').then(m => ({
  default: m.Home
})));
const Gallery = lazy(() => import('./pages/Gallery').then(m => ({
  default: m.Gallery
})));
const About = lazy(() => import('./pages/About').then(m => ({
  default: m.About
})));
const Blog = lazy(() => import('./pages/Blog').then(m => ({
  default: m.Blog
})));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({
  default: m.BlogPost
})));
const Contact = lazy(() => import('./pages/Contact').then(m => ({
  default: m.Contact
})));
const Toolkits = lazy(() => import('./pages/Toolkits').then(m => ({
  default: m.Toolkits
})));
const NetworkingGuide = lazy(() => import('./pages/NetworkingGuide').then(m => ({
  default: m.NetworkingGuide
})));
const FAQ = lazy(() => import('./pages/FAQ').then(m => ({
  default: m.FAQ
})));
const Partners = lazy(() => import('./pages/Partners').then(m => ({
  default: m.Partners
})));
const Events = lazy(() => import('./pages/Events').then(m => ({
  default: m.Events
})));
const SuccessStories = lazy(() => import('./pages/SuccessStories').then(m => ({
  default: m.SuccessStories
})));
const PartnerWithUs = lazy(() => import('./pages/PartnerWithUs').then(m => ({
  default: m.PartnerWithUs
})));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({
  default: m.PrivacyPolicy
})));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({
  default: m.TermsOfService
})));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy').then(m => ({
  default: m.CookiePolicy
})));
const Portfolio = lazy(() => import('./pages/Portfolio').then(m => ({
  default: m.Portfolio
})));
const Membership = lazy(() => import('./pages/Membership').then(m => ({
  default: m.Membership
})));
const JoinTeam = lazy(() => import('./pages/JoinTeam').then(m => ({
  default: m.JoinTeam
})));
const JoinNow = lazy(() => import('./pages/JoinNow').then(m => ({
  default: m.JoinNow
})));
const InnerCircle = lazy(() => import('./pages/InnerCircle').then(m => ({
  default: m.InnerCircle
})));
const LifetimePartner = lazy(() => import('./pages/LifetimePartner').then(m => ({
  default: m.LifetimePartner
})));
const WaitingRoom = lazy(() => import('./pages/WaitingRoom').then(m => ({
  default: m.WaitingRoom
})));
const WarriEvent2025 = lazy(() => import('./pages/WarriEvent2025').then(m => ({
  default: m.WarriEvent2025
})));
const HowToEngage = lazy(() => import('./pages/HowToEngage').then(m => ({
  default: m.HowToEngage
})));
const HostSalon = lazy(() => import('./pages/HostSalon').then(m => ({
  default: m.HostSalon
})));
const Spotlight = lazy(() => import('./pages/Spotlight').then(m => ({
  default: m.Spotlight
})));
const BibiEnejeSpotlight = lazy(() => import('./pages/BibiEnejeSpotlight').then(m => ({
  default: m.BibiEnejeSpotlight
})));
const AugustineSpotlight = lazy(() => import('./pages/AugustineSpotlight').then(m => ({
  default: m.AugustineSpotlight
})));
const DrOsagieSpotlight = lazy(() => import('./pages/DrOsagieSpotlight').then(m => ({
  default: m.DrOsagieSpotlight
})));
const SalonSpotlight = lazy(() => import('./pages/SalonSpotlight').then(m => ({
  default: m.SalonSpotlight
})));
const Team = lazy(() => import('./pages/Team').then(m => ({
  default: m.Team
})));
const TeamMemberDetail = lazy(() => import('./pages/TeamMemberDetail').then(m => ({
  default: m.TeamMemberDetail
})));
const AIPowerBundle = lazy(() => import('./pages/AIPowerBundle').then(m => ({
  default: m.AIPowerBundle
})));
const AIMasterclass = lazy(() => import('./pages/AIMasterclass').then(m => ({
  default: m.AIMasterclass
})));
const AIFormTest = lazy(() => import('./pages/AIFormTest').then(m => ({
  default: m.AIFormTest
})));
const AIKids = lazy(() => import('./pages/AIKids').then(m => ({
  default: m.AIKids
})));
const KidsAIProgram = lazy(() => import('./pages/KidsAIProgram').then(m => ({
  default: m.KidsAIProgram
})));
const Training = lazy(() => import('./pages/Training').then(m => ({
  default: m.Training
})));
const LoungeOnboard = lazy(() => import('./pages/LoungeOnboard').then(m => ({
  default: m.LoungeOnboard
})));
const AIClass = lazy(() => import('./pages/AIClass').then(m => ({
  default: m.AIClass
})));
const GreenEvent = lazy(() => import('./pages/GreenEvent').then(m => ({
  default: m.GreenEvent
})));
const GreenSpeakerDetail = lazy(() => import('./pages/GreenSpeakerDetail').then(m => ({
  default: m.GreenSpeakerDetail
})));
// ScrollToTop component that scrolls to top on route change
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
export function App() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isHostFormOpen, setIsHostFormOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isCorporateTrainingOpen, setIsCorporateTrainingOpen] = useState(false);
  // Preload Paystack script so it's cached before user reaches /class or payment pages
  useEffect(() => {
    // Only preload if not already present
    if (!document.querySelector('link[href*="paystack"]')) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = 'https://js.paystack.co/v1/inline.js';
      link.as = 'script';
      document.head.appendChild(link);
    }
  }, []);
  const openRegistrationForm = () => {
    setIsFormOpen(true);
  };
  const openHostSalonForm = () => {
    setIsHostFormOpen(true);
  };
  const openPaymentForm = () => {
    setIsPaymentOpen(true);
  };
  const openCorporateTrainingForm = () => {
    setIsCorporateTrainingOpen(true);
  };
  useEffect(() => {
    window.openRegistrationForm = openRegistrationForm;
    window.openHostSalonForm = openHostSalonForm;
    window.openPaymentForm = openPaymentForm;
    window.openCorporateTrainingForm = openCorporateTrainingForm;
    return () => {
      delete window.openRegistrationForm;
      delete window.openHostSalonForm;
      delete window.openPaymentForm;
      delete window.openCorporateTrainingForm;
    };
  }, []);
  return <Router data-id="element-0" data-mp="343vdca7h8qlx">
      <ScrollToTop data-id="element-1" data-mp="343lnqnqcglgr" />
      <ErrorBoundary data-id="element-2" data-mp="344wzpaty0ziy">
        <Suspense fallback={<PageLoader data-id="element-4" data-mp="345yd3lyfly3u" />} data-id="element-3" data-mp="345eqvt1r0nk0">
          <Routes data-id="element-5" data-mp="2pf7jc8da4oy3">
            <Route path="/lounge" element={<LoungeOnboard data-id="element-7" data-mp="2pgip2dqac6ru" />} data-id="element-6" data-mp="2pfqr4w97twye" />
            <Route path="/class" element={<AIClass data-id="element-9" data-mp="2udiqucg3qnd5" />} data-id="element-8" data-mp="2pd4n36lj9zkx" />
            <Route path="/*" element={<Layout openRegistrationForm={openRegistrationForm} openHostSalonForm={openHostSalonForm} openPaymentForm={openPaymentForm} data-id="element-11" data-mp="2gdrba79xsyx1">
                  <Suspense fallback={<PageLoader data-id="element-13" data-mp="30uvegmavv8k0" />} data-id="element-12" data-mp="30uc7u1qoo6la">
                    <Routes data-id="element-14" data-mp="30t11pv9rmmr0">
                      <Route path="/" element={<Home openRegistrationForm={openRegistrationForm} openHostSalonForm={openHostSalonForm} openPaymentForm={openPaymentForm} data-id="element-16" data-mp="2qwo970pyiexu" />} data-id="element-15" data-mp="2vvr43sc834s3" />
                      <Route path="/green" element={<GreenEvent data-id="element-18" data-mp="2d487q4cpeoz1" />} data-id="element-17" data-mp="2d5j5nnjgx2i8" />
                      <Route path="/green/speaker/:id" element={<GreenSpeakerDetail data-id="element-20" data-mp="284dh81wuc1ei" />} data-id="element-19" data-mp="286pwg7b7rxnv" />
                      <Route path="/gallery" element={<Gallery data-id="element-22" data-mp="1y8iqskmdl216" />} data-id="element-21" data-mp="2396diu3kmwpl" />
                      <Route path="/about" element={<About data-id="element-24" data-mp="1y80igpmfuxcw" />} data-id="element-23" data-mp="1y8s9ro9068xd" />
                      <Route path="/team" element={<Team data-id="element-26" data-mp="1u6oueoj7qk6t" />} data-id="element-25" data-mp="1u6x5cavrzffd" />
                      <Route path="/team/:id" element={<TeamMemberDetail data-id="element-28" data-mp="9owggi2cyip6" />} data-id="element-27" data-mp="1u7gmvqhik1sv" />
                      <Route path="/blog" element={<Blog data-id="element-30" data-mp="9oml0eyjnjc3" />} data-id="element-29" data-mp="9nuuvjnpudt7" />
                      <Route path="/blog/:slug" element={<BlogPost data-id="element-32" data-mp="dpg8x8bzky4z" />} data-id="element-31" data-mp="dq7vxuripnjn" />
                      <Route path="/contact" element={<Contact data-id="element-34" data-mp="ipucad2n7jwo" />} data-id="element-33" data-mp="dskmuxf9788u" />
                      <Route path="/toolkits" element={<Toolkits data-id="element-36" data-mp="inrlvrxkvorf" />} data-id="element-35" data-mp="iqvwp85jtir2" />
                      <Route path="/networking-guide" element={<NetworkingGuide data-id="element-38" data-mp="3m6kt7mzrjhd5" />} data-id="element-37" data-mp="np747c8dfh64" />
                      <Route path="/faq" element={<FAQ openRegistrationForm={openRegistrationForm} data-id="element-40" data-mp="3r3b7q5vx64wg" />} data-id="element-39" data-mp="3m486yxmz2kua" />
                      <Route path="/partners" element={<Partners data-id="element-42" data-mp="4quunpdjs5a0" />} data-id="element-41" data-mp="4pjj33dv1t8c" />
                      <Route path="/events" element={<Events data-id="element-44" data-mp="33g33lra891w0" />} data-id="element-43" data-mp="4rwa061ljdxj" />
                      <Route path="/events/warri-2025" element={<WarriEvent2025 data-id="element-46" data-mp="38duymu66544f" />} data-id="element-45" data-mp="33f0i5yvobrnh" />
                      <Route path="/success-stories" element={<SuccessStories data-id="element-48" data-mp="2f9hqfa1as9pm" />} data-id="element-47" data-mp="2f7dlqpvkr6oa" />
                      <Route path="/portfolio" element={<Portfolio data-id="element-50" data-mp="2p5b97b5m0zfu" />} data-id="element-49" data-mp="2k4n8fxrkx48f" />
                      <Route path="/membership" element={<Membership data-id="element-52" data-mp="2p62rjka750o6" />} data-id="element-51" data-mp="2p6cl1hug9pym" />
                      <Route path="/join-team" element={<JoinTeam data-id="element-54" data-mp="2u32zy1uiqfig" />} data-id="element-53" data-mp="2u44b06bjb206" />
                      <Route path="/joinnow" element={<JoinNow data-id="element-56" data-mp="1wh5o2nan98fh" />} data-id="element-55" data-mp="2u2bc5d75xm2q" />
                      <Route path="/inner-circle" element={<InnerCircle data-id="element-58" data-mp="1weswrjizxlpr" />} data-id="element-57" data-mp="1whyol6j0f1p6" />
                      <Route path="/lifetime-partner" element={<LifetimePartner data-id="element-60" data-mp="26e9zpt6v5li1" />} data-id="element-59" data-mp="21exbb40cg6dc" />
                      <Route path="/waiting-room" element={<WaitingRoom data-id="element-62" data-mp="2b5s0oh8vzm30" />} data-id="element-61" data-mp="26cyurq1om7pc" />
                      <Route path="/join-the-salon" element={<HowToEngage data-id="element-64" data-mp="1dqe4yu0uu3nw" />} data-id="element-63" data-mp="2b6usqshy3uki" />
                      <Route path="/host-salon" element={<HostSalon data-id="element-66" data-mp="1dobigjywusnx" />} data-id="element-65" data-mp="1dpcvgtzhlp8a" />
                      <Route path="/spotlight" element={<Spotlight data-id="element-68" data-mp="1ino5p5tp21r1" />} data-id="element-67" data-mp="1iofofg2704zw" />
                      <Route path="/salon-spotlight" element={<SalonSpotlight data-id="element-70" data-mp="3vx635v19u1ei" />} data-id="element-69" data-mp="3vzig1ts69lkp" />
                      <Route path="/spotlight/bibi-eneje" element={<BibiEnejeSpotlight data-id="element-72" data-mp="3mxiyn0d8wfh8" />} data-id="element-71" data-mp="3rz89aewk2r4s" />
                      <Route path="/spotlight/augustine-aigbokhaevbo" element={<AugustineSpotlight data-id="element-74" data-mp="3d164609vlxlf" />} data-id="element-73" data-mp="3i0910xfpcxib" />
                      <Route path="/spotlight/dr-osagie-alex" element={<DrOsagieSpotlight data-id="element-76" data-mp="3883a7r16sm4p" />} data-id="element-75" data-mp="3d2h7y03bn5cj" />
                      <Route path="/partner-with-us" element={<PartnerWithUs openRegistrationForm={openRegistrationForm} openHostSalonForm={openHostSalonForm} data-id="element-78" data-mp="2z75515f0i8kj" />} data-id="element-77" data-mp="348ufojybok83" />
                      <Route path="/privacy-policy" element={<PrivacyPolicy data-id="element-80" data-mp="2kn2e96je0gxy" />} data-id="element-79" data-mp="2fn6j7wso3zuj" />
                      <Route path="/terms-of-service" element={<TermsOfService data-id="element-82" data-mp="26okxjsrwg4pj" />} data-id="element-81" data-mp="2kkg9tfrsmhjy" />
                      <Route path="/cookie-policy" element={<CookiePolicy data-id="element-84" data-mp="2aqfukl2srvxi" />} data-id="element-83" data-mp="2apege7qhitbk" />
                      <Route path="/ai-power-bundle" element={<AIPowerBundle data-id="element-86" data-mp="33cr44l8dt76g" />} data-id="element-85" data-mp="2ydfnrdbxg3pe" />
                      <Route path="/ai-masterclass" element={<AIMasterclass data-id="element-88" data-mp="2pl46oi2pdsm3" />} data-id="element-87" data-mp="33f4yssz5ghdh" />
                      <Route path="/ai-form-test" element={<AIFormTest data-id="element-90" data-mp="2tledfwybkn20" />} data-id="element-89" data-mp="2tmfqg6ywbjmd" />
                      <Route path="/ai-kids" element={<AIKids data-id="element-92" data-mp="3hbqtxw7mrs47" />} data-id="element-91" data-mp="3hd1xbux5yxus" />
                      <Route path="/kids-ai-program" element={<KidsAIProgram data-id="element-94" data-mp="3m538temickjr" />} data-id="element-93" data-mp="3hap60r5inb52" />
                      <Route path="/training" element={<Training data-id="element-96" data-mp="1amylwxdafbvo" />} data-id="element-95" data-mp="1anri54emeizk" />
                    </Routes>
                  </Suspense>
                </Layout>} data-id="element-10" data-mp="2uety4m8rad90" />
          </Routes>
        </Suspense>
      </ErrorBoundary>

      <Dialog isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Join the Salon" size="xl" data-id="element-97" data-mp="1kdkva9uev65x">
        <div className="w-full" data-id="element-98" data-mp="rpf7fe82911v">
          <iframe src="https://docs.google.com/forms/d/e/1FAIpQLSdr_5jvYO4hJncEC0hjHQweFJSh8FFpzrBhsjQvCQemuZT7MA/viewform?embedded=true" width="100%" height="600" frameBorder="0" marginHeight={0} marginWidth={0} data-id="element-99" data-mp="rqgkfo8mzxm8">
            Loading…
          </iframe>
        </div>
      </Dialog>
      <HostSalonDialog isOpen={isHostFormOpen} onClose={() => setIsHostFormOpen(false)} data-id="element-100" data-mp="2d2rtf7pydtqn" />
      <PaymentDialog isOpen={isPaymentOpen} onClose={() => setIsPaymentOpen(false)} data-id="element-101" data-mp="284q9ylom7sen" />
      <CorporateTrainingDialog isOpen={isCorporateTrainingOpen} onClose={() => setIsCorporateTrainingOpen(false)} data-id="element-102" data-mp="hnl7mgs94ny8" />
    </Router>;
}